import { useState } from "react";
import { styled } from "@mui/material";
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { HtmlReactParser, translate } from "@kaltura/mediaspace-shared-utils";
import { Truncate, generalHtmlStyles } from "@kaltura/mediaspace-shared-ui";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface ContentPolicyProps {
    // Title of the modal box with full policy text
    title: string;
    // Content policy HTML
    policyHtml: string;
}

const StyledAlert = styled(Alert)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const StyledCustomHtml = styled("div")(({ theme }) => generalHtmlStyles(theme));

/**
 * Content policy acknowledgement for DS media page.
 */
export function ContentPolicy({ title, policyHtml }: ContentPolicyProps) {
    const [showAlert, setShowAlert] = useState(true);
    const [showModal, setShowModal] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = (action: string, buttonType: number) => {
        sendButtonAnalytics(`Media Policy - ${action}`, buttonType);
    };

    if (!showAlert) {
        return null;
    }

    return (
        <>
            <StyledAlert
                className="kms-ds-media-page-metadata-content-policy"
                severity="info"
                onClose={() => {
                    setShowAlert(false);
                    sendAnalytics("Close Alert", ButtonClickAnalyticsType.CLOSE);
                }}
            >
                <StyledCustomHtml>
                    <Truncate
                        lines={3}
                        onToggle={() => {
                            setShowModal(true);
                            sendAnalytics("Read more", ButtonClickAnalyticsType.EXPAND);
                        }}
                        showMoreTranslatedText={translate("Read More")}
                        preventStateUpdate={true}
                    >
                        {HtmlReactParser(policyHtml)}
                    </Truncate>
                </StyledCustomHtml>
            </StyledAlert>

            <Modal open={showModal} onClose={() => setShowModal(false)}>
                <ModalTitle>{title}</ModalTitle>
                <ModalContent>
                    <StyledCustomHtml>{HtmlReactParser(policyHtml)}</StyledCustomHtml>
                </ModalContent>
                <ModalActions>
                    <Button
                        variant={"borderless"}
                        color={"secondary"}
                        onClick={() => {
                            setShowModal(false);
                            sendAnalytics(" Close modal box", ButtonClickAnalyticsType.CLOSE);
                        }}
                    >
                        {translate("Close")}
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
}

export default ContentPolicy;
